/**
 * Dynamic Grid Strategy
 * Creates multiple chase orders at different tick distances
 *
 * Features:
 * - Up to 3 levels per side (6 orders total)
 * - Auto-multiplied tick distance (e.g., 10 -> 10, 20, 30 ticks)
 * - Range bounds: If price exits range, chase stops
 * - Partial fills: When one level fills, others continue
 * - State persistence across page refresh
 */

export class GridStrategy {
    constructor() {
        this.activeGrids = new Map(); // gridId -> gridState
        this.gridHistory = []; // Completed/stopped grids
        this._isStarting = false; // Mutex to prevent double-start
    }

    /**
     * Start a new grid strategy
     * @param {Object} config
     * @param {string} config.asset - Asset symbol
     * @param {number} config.levels - Number of levels per side (1-3)
     * @param {number} config.baseTickDistance - Base tick distance (levels multiply: 10, 20, 30)
     * @param {number} config.sizePerLevel - Size for each order
     * @param {number} config.upperRange - Upper price bound (optional)
     * @param {number} config.lowerRange - Lower price bound (optional)
     * @param {Object} config.chaseSettings - frequencyRangeMin, frequencyRangeMax
     */
    async startGrid(config) {
        // Mutex: prevent double-click from starting multiple grids
        if (this._isStarting) {
            // console.log('[GRID] startGrid already in progress, ignoring duplicate call');
            return { success: true, duplicate: true };  // Silent ignore, no error
        }
        this._isStarting = true;

        try {
            const gridId = `grid-${Date.now()}`;

            const gridState = {
                gridId,
                asset: config.asset,
                levels: config.levels,
                baseTickDistance: config.baseTickDistance,
                sizePerLevel: config.sizePerLevel,
                anchor: config.anchor,  // Long below anchor, Short above anchor
                chaseSettings: config.chaseSettings,
                orders: [], // { side, level, tickDistance, oid, chaseId, status }
                status: 'active',
                startTime: Date.now()
            };

            // console.log(`[GRID] Starting grid ${gridId} for ${config.asset}`);
            // console.log(`[GRID] Levels: ${config.levels}, Base tick: ${config.baseTickDistance}, Size: ${config.sizePerLevel}`);

            // Get current best bid/ask for price calculation
            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(config.asset);
            if (!bidAsk || !bidAsk.bestBid || !bidAsk.bestAsk) {
                return { success: false, error: 'Failed to get market prices' };
            }

            const bestBid = bidAsk.bestBid;
            const bestAsk = bidAsk.bestAsk;

            // Get tick size
            await window.BrowserHyperliquidClient.getAssetIndex(config.asset);
            const tickSize = this._calculateTickSize(bestBid, bestAsk);

            // Build all orders for batch placement
            const ordersToPlace = [];
            const orderMeta = []; // Track metadata for each order

            const anchor = config.anchor || bidAsk.mid;  // Default to mid price if no anchor
            // console.log(`[GRID] Anchor price: ${anchor}`);

            for (let level = 1; level <= config.levels; level++) {
                const tickDistance = config.baseTickDistance * level;

                // BUY order price - only place if below anchor
                const buyPrice = window.BrowserHyperliquidClient.roundToTickSize(bestBid - (tickDistance * tickSize));
                if (buyPrice < anchor) {
                    ordersToPlace.push({
                        asset: config.asset,
                        side: 'buy',
                        price: buyPrice,
                        size: config.sizePerLevel,
                        reduceOnly: false
                    });
                    orderMeta.push({ side: 'buy', level, tickDistance, price: buyPrice });
                } else {
                    // console.log(`[GRID] Skipping BUY level ${level}: price ${buyPrice} >= anchor ${anchor}`);
                }

                // SELL order price - only place if above anchor
                const sellPrice = window.BrowserHyperliquidClient.roundToTickSize(bestAsk + (tickDistance * tickSize));
                if (sellPrice > anchor) {
                    ordersToPlace.push({
                        asset: config.asset,
                        side: 'sell',
                        price: sellPrice,
                        size: config.sizePerLevel,
                        reduceOnly: false
                    });
                    orderMeta.push({ side: 'sell', level, tickDistance, price: sellPrice });
                } else {
                    // console.log(`[GRID] Skipping SELL level ${level}: price ${sellPrice} <= anchor ${anchor}`);
                }
            }

            // console.log(`[GRID] Batch placing ${ordersToPlace.length} orders...`);

            // Batch place all orders at once
            const batchResult = await window.BrowserHyperliquidClient.batchPlace(ordersToPlace);

            // Parse results and extract OIDs
            const statuses = batchResult?.response?.data?.statuses || [];
            const errors = [];

            for (let i = 0; i < orderMeta.length; i++) {
                const meta = orderMeta[i];
                const status = statuses[i];

                const oid = status?.resting?.oid || status?.filled?.oid;
                if (oid) {
                    // Start chase for this order with anchor constraint
                    // Anchor constraint: BUY orders cannot cross above anchor, SELL orders cannot cross below anchor
                    const chase = await this._startChaseForOrder(
                        { oid, price: meta.price, side: meta.side, size: config.sizePerLevel },
                        config.asset,
                        {
                            tickDistance: meta.tickDistance,
                            ...config.chaseSettings,
                            anchorPrice: config.anchor,
                            useAnchor: config.anchor !== null && config.anchor !== undefined
                        }
                    );

                    gridState.orders.push({
                        side: meta.side,
                        level: meta.level,
                        tickDistance: meta.tickDistance,
                        oid: oid,
                        price: meta.price,
                        chaseId: chase?.chaseId || null,
                        status: chase?.success ? 'active' : 'failed'
                    });
                    // console.log(`[GRID] ${meta.side.toUpperCase()} level ${meta.level}: ${meta.tickDistance} ticks, OID ${oid}, chase ${chase?.chaseId}`);
                } else {
                    const errorMsg = status?.error || 'Unknown error';
                    // console.error(`[GRID] Failed to place ${meta.side.toUpperCase()} order at level ${meta.level}: ${errorMsg}`);
                    errors.push(`${meta.side.toUpperCase()} L${meta.level}: ${errorMsg}`);
                }
            }

            // Check if any orders were placed
            const activeOrders = gridState.orders.filter(o => o.status === 'active');
            if (activeOrders.length === 0) {
                // console.error(`[GRID] No orders placed successfully`);
                const errorMsg = errors.length > 0 ? errors[0] : 'Failed to place any orders';
                return { success: false, error: errorMsg };
            }

            this.activeGrids.set(gridId, gridState);
            this._saveState();

            // Start monitoring for fills
            this._startGridMonitoring(gridId);

            // console.log(`[GRID] Grid started: ${gridState.orders.length} orders placed`);

            // Dispatch event
            window.dispatchEvent(new CustomEvent('grid-started', {
                detail: { gridId, ordersPlaced: gridState.orders.length }
            }));

            return { success: true, gridId, ordersPlaced: gridState.orders.length };

        } catch (error) {
            // console.error('[GRID] Error starting grid:', error);
            return { success: false, error: error.message };
        } finally {
            // ALWAYS reset mutex, even if there's an unexpected error
            this._isStarting = false;
        }
    }

    async _placeGridOrder(asset, side, size, tickDistance) {
        try {
            // Get current best bid/ask for price calculation
            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(asset);
            if (!bidAsk || !bidAsk.bestBid || !bidAsk.bestAsk) {
                // console.error('[GRID] Invalid bid/ask data');
                return { success: false, error: 'Invalid bid/ask data' };
            }

            const bestBid = bidAsk.bestBid;
            const bestAsk = bidAsk.bestAsk;

            // Get tick size (also caches szDecimals)
            await window.BrowserHyperliquidClient.getAssetIndex(asset);
            const tickSize = this._calculateTickSize(bestBid, bestAsk);

            // Calculate price: tickDistance ticks away from best
            let price;
            if (side === 'buy') {
                price = bestBid - (tickDistance * tickSize);
            } else {
                price = bestAsk + (tickDistance * tickSize);
            }

            // Round to tick size
            price = window.BrowserHyperliquidClient.roundToTickSize(price);

            // console.log(`[GRID] Placing ${side} order: ${size} @ ${price} (${tickDistance} ticks from market)`);

            // Place order
            const result = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: side,
                size,
                price,
                orderType: 'limit',
                timeInForce: 'GTC',
                reduceOnly: false
            });

            if (result.success) {
                const oid = result.result?.response?.data?.statuses?.[0]?.resting?.oid;
                if (oid) {
                    return { success: true, order: { oid, price, side, size } };
                }
                // Order might have filled immediately - check for filled status
                const filled = result.result?.response?.data?.statuses?.[0]?.filled;
                if (filled) {
                    // console.log(`[GRID] Order filled immediately: ${filled.oid}`);
                    return { success: true, order: { oid: filled.oid, price, side, size } };
                }
                // console.error('[GRID] No OID in response:', JSON.stringify(result.result));
                return { success: false, error: 'No order ID returned' };
            }

            // console.error('[GRID] Order placement failed:', result.error);
            return { success: false, error: result.error || 'Order placement failed' };

        } catch (error) {
            // console.error('[GRID] Error placing order:', error);
            return { success: false, error: error.message || 'Unknown error' };
        }
    }

    _calculateTickSize(bestBid, bestAsk) {
        // Calculate tick size from price decimals
        const bidStr = String(bestBid);
        const askStr = String(bestAsk);
        const bidDecimals = bidStr.includes('.') ? bidStr.split('.')[1].length : 0;
        const askDecimals = askStr.includes('.') ? askStr.split('.')[1].length : 0;
        const decimals = Math.max(bidDecimals, askDecimals);
        return Math.pow(10, -decimals);
    }

    async _startChaseForOrder(order, asset, settings) {
        try {
            return await window.BrowserChaseTracker.startChase(
                order.oid,
                {
                    coin: asset,
                    side: order.side === 'buy' ? 'B' : 'A',
                    sz: order.size,
                    limitPx: order.price
                },
                settings
            );
        } catch (error) {
            // console.error('[GRID] Error starting chase:', error);
            return { success: false, error: error.message };
        }
    }

    _startGridMonitoring(gridId) {
        // Listen for chase-stopped events to track fills
        const listener = (event) => {
            const { chaseId, reason } = event.detail;
            const grid = this.activeGrids.get(gridId);
            if (!grid) return;

            // Find matching order
            const order = grid.orders.find(o => o.chaseId === chaseId);
            if (order) {
                const oldStatus = order.status;
                if (reason === 'order_filled') {
                    order.status = 'filled';
                } else if (reason === 'range_exceeded') {
                    order.status = 'stopped_range';
                } else if (reason === 'user_stopped') {
                    order.status = 'stopped';
                } else {
                    order.status = 'stopped';
                }

                // console.log(`[GRID] Order ${order.side} L${order.level} status: ${oldStatus} -> ${order.status} (${reason})`);

                this._saveState();
                this._checkGridCompletion(gridId);
            }
        };

        window.addEventListener('chase-stopped', listener);

        // Store listener for cleanup
        const grid = this.activeGrids.get(gridId);
        if (grid) {
            grid.stopListener = listener;
        }

        // console.log(`[GRID] Monitoring started for grid ${gridId}`);
    }

    _checkGridCompletion(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) return;

        const activeOrders = grid.orders.filter(o => o.status === 'active');
        const filledOrders = grid.orders.filter(o => o.status === 'filled');

        // console.log(`[GRID] Grid ${gridId}: ${activeOrders.length} active, ${filledOrders.length} filled`);

        if (activeOrders.length === 0) {
            // All orders filled or stopped
            grid.status = 'completed';
            grid.endTime = Date.now();

            // Cleanup listener
            if (grid.stopListener) {
                window.removeEventListener('chase-stopped', grid.stopListener);
                grid.stopListener = null;
            }

            // Move to history
            this.gridHistory.push(grid);
            this.activeGrids.delete(gridId);

            // console.log(`[GRID] Grid ${gridId} completed: ${filledOrders.length} orders filled`);

            // Dispatch event
            window.dispatchEvent(new CustomEvent('grid-completed', {
                detail: { gridId, orders: grid.orders }
            }));

            this._saveState();
        }
    }

    async stopGrid(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) {
            // console.error(`[GRID] Grid not found: ${gridId}`);
            return { success: false, error: 'Grid not found' };
        }

        // console.log(`[GRID] Stopping grid ${gridId}...`);

        // Stop all active chases
        for (const order of grid.orders) {
            if (order.status === 'active' && order.chaseId) {
                // console.log(`[GRID] Stopping chase ${order.chaseId}...`);
                await window.BrowserChaseTracker.stopChase(order.chaseId, 'user_stopped');
            }
        }

        // Cancel remaining orders
        for (const order of grid.orders) {
            if (order.status === 'active' && order.oid) {
                // console.log(`[GRID] Cancelling order ${order.oid}...`);
                await window.BrowserHyperliquidClient.cancelOrder({
                    orderId: order.oid,
                    asset: grid.asset
                });
                order.status = 'cancelled';
            }
        }

        grid.status = 'stopped';
        grid.endTime = Date.now();

        // Cleanup listener
        if (grid.stopListener) {
            window.removeEventListener('chase-stopped', grid.stopListener);
            grid.stopListener = null;
        }

        // Move to history
        this.gridHistory.push(grid);
        this.activeGrids.delete(gridId);

        this._saveState();

        // console.log(`[GRID] Grid ${gridId} stopped`);

        // Dispatch event
        window.dispatchEvent(new CustomEvent('grid-stopped', {
            detail: { gridId }
        }));

        return { success: true };
    }

    getStatus() {
        const grids = {};
        for (const [gridId, grid] of this.activeGrids) {
            grids[gridId] = {
                asset: grid.asset,
                status: grid.status,
                levels: grid.levels,
                orders: grid.orders.map(o => ({
                    side: o.side,
                    level: o.level,
                    status: o.status,
                    tickDistance: o.tickDistance
                })),
                activeCount: grid.orders.filter(o => o.status === 'active').length,
                filledCount: grid.orders.filter(o => o.status === 'filled').length
            };
        }
        return {
            activeGrids: grids,
            count: this.activeGrids.size
        };
    }

    getActiveGrids() {
        return Array.from(this.activeGrids.values());
    }

    _saveState() {
        try {
            const data = {};
            for (const [gridId, grid] of this.activeGrids) {
                const { stopListener, ...serializable } = grid;
                data[gridId] = serializable;
            }
            localStorage.setItem('activeGrids', JSON.stringify(data));

            // Save history (keep last 20)
            const recentHistory = this.gridHistory.slice(-20);
            localStorage.setItem('gridHistory', JSON.stringify(recentHistory));
        } catch (error) {
            // console.error('[GRID] Error saving state:', error);
        }
    }

    restoreState() {
        try {
            const saved = localStorage.getItem('activeGrids');
            if (saved) {
                const data = JSON.parse(saved);
                for (const [gridId, grid] of Object.entries(data)) {
                    if (grid.status === 'active') {
                        this.activeGrids.set(gridId, grid);
                        this._startGridMonitoring(gridId);
                        // console.log(`[GRID] Restored grid ${gridId}: ${grid.orders.filter(o => o.status === 'active').length} active orders`);
                    }
                }
            }

            const history = localStorage.getItem('gridHistory');
            if (history) {
                this.gridHistory = JSON.parse(history);
            }
        } catch (error) {
            // console.error('[GRID] Error restoring state:', error);
        }
    }
}

// Create singleton instance (only if not already created)
if (typeof window !== 'undefined') {
    if (!window.GridStrategy) {
        window.GridStrategy = new GridStrategy();
        window.GridStrategy.restoreState();
        // console.log('[GRID] GridStrategy initialized');
    }
}
